import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Submission } from "./SubmissionService";
import { Form } from "./FormService";

// GraphQL queries
const SUBMISSION_FILE_LIST_QUERY = gql`
  query SubmissionFileList(
    $filter: SubmissionFileFilter
    $params: PaginationAndSorting
  ) {
    submissionFileList(filter: $filter, params: $params) {
      code
      data {
        id
        submissionId
        formId
        formFieldName
        fileName
        mimeType
        fileSize
        fileUrl
        errorDescription
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_FILE_GET_QUERY = gql`
  query SubmissionFileGet($id: ID) {
    submissionFileGet(id: $id) {
      code
      data {
        id
        submissionId
        formId
        formFieldName
        fileName
        mimeType
        fileSize
        fileUrl
        errorDescription
        status
        createdAt
        updatedAt
        submission {
          id
          formId
          submissionDttm
          status
        }
        form {
          id
          name
          path
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_FILE_GET_MANY_QUERY = gql`
  query SubmissionFileGetMany($ids: [ID]) {
    submissionFileGetMany(ids: $ids) {
      code
      data {
        id
        submissionId
        formId
        formFieldName
        fileName
        mimeType
        fileSize
        fileUrl
        errorDescription
        status
        createdAt
        updatedAt
        submission {
          id
          formId
          submissionDttm
          status
        }
        form {
          id
          name
          path
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const SUBMISSION_FILE_UPDATE_MUTATION = gql`
  mutation SubmissionFileUpdate(
    $where: SubmissionFileWhereInput
    $params: SubmissionFileInput
  ) {
    submissionFileUpdate(where: $where, params: $params) {
      code
      data {
        id
        submissionId
        formId
        formFieldName
        fileName
        mimeType
        fileSize
        fileUrl
        errorDescription
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_FILE_REMOVE_MUTATION = gql`
  mutation SubmissionFileRemove($where: SubmissionFileWhereInput) {
    submissionFileRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_FILE_REMOVE_MANY_MUTATION = gql`
  mutation SubmissionFileRemoveMany($where: [SubmissionFileWhereInput]) {
    submissionFileRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface SubmissionFile {
  id?: string | undefined;
  submissionId?: string | undefined;
  formId?: string | undefined;
  formFieldName?: string | undefined;
  fileName?: string | undefined;
  mimeType?: string | undefined;
  fileSize?: string | undefined;
  fileUrl?: string | undefined;
  errorDescription?: string | undefined;
  status?: number | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  submission?: Submission;
  form?: Form;
}

export interface SubmissionFileInput {
  id?: string | null | undefined;
  submissionId?: string | null | undefined;
  formId?: string | null | undefined;
  fileName?: string | null | undefined;
  mimeType?: string | null | undefined;
  fileSize?: string | null | undefined;
  fileUrl?: string | null | undefined;
  status?: number | null | undefined;
}

export interface SubmissionFileFilter {
  id?: string[] | null | undefined;
  formId?: string[] | null | undefined;
  submissionId?: string[] | null | undefined;
  fileName?: string[] | null | undefined;
  mimeType?: string[] | null | undefined;
  status?: number[] | null | undefined;
}

export interface SubmissionFileWhereInput {
  id?: string | undefined;
}

/**
 * Submission File service that handles all submission file-related API calls
 * Supports operations: list, get, getMany, update, remove, removeMany
 */
export class SubmissionFileService {
  /**
   * Get a list of submission files with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: SubmissionFileFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_FILE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single submission file by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_FILE_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple submission files by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SUBMISSION_FILE_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Update an existing submission file
   */
  static async update(
    where: SubmissionFileWhereInput,
    params: SubmissionFileInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SUBMISSION_FILE_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a submission file
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SUBMISSION_FILE_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple submission files
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      SUBMISSION_FILE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
